<template>
  <vddl-draggable
    :class="{ opened: getOpen === id, popupon: isPopoverActive }"
    class="component"
    :draggable="component"
    :index="index"
    :disable-if="isdisabled || !canEdit"
    :wrapper="list"
    :moved="onMove"
    :canceled="onCancel"
    :dragstart="onDragStart"
    :dragend="onDragEnd"
    :type="type"
  >
    <div
      v-if="getIsRecursiveContainer(id)"
      class="folder_container"
      :class="{ collapsed: isCollapsed, empty: !hasChildren }"
      :draggable="!isdisabled && canEdit"
      @dragstart="onComponentStartDrag($event)"
    >
      <div
        class="folder"
        :class="{ selected: isSelected }"
        @contextmenu.prevent="onContextMenu($event)"
      >
        <div class="arrow" @click.stop="toggle(component)">
          <svg-icon :width="'8'" :height="'8'" icon="app-icons/arrow-down" />
        </div>
        <div class="icon">
          <svg-icon
            ref="icon"
            :width="'16'"
            :height="'16'"
            icon="app-icons/folder"
            @on-load="onIconLoad"
          />
        </div>

        <input
          v-if="isEditing"
          ref="nameInput"
          class="nameinput"
          @blur="onBlurText($event, component)"
          @keyup.enter="onBlurText($event, component)"
        />
        <span
          v-else
          type="text"
          class="name"
          @dblclick="onDblclickText($event, component)"
        >
          {{ component.name }}
        </span>
        <VDropdown
          v-if="canEdit"
          popper-class="v-popper--compListItem-dropdown"
          placement="bottom-start"
        >
          <button type="button" class="btn-add">
            <svg-icon :width="'11'" :height="'11'" icon="app-icons/create" />
          </button>
          <template #popper>
            <div class="menu">
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateComponent"
              >
                Create Component
              </button>
              <button
                v-close-popper
                type="button"
                class="option"
                @click="onClickCreateFolder"
              >
                Create Folder
              </button>
            </div>
          </template>
        </VDropdown>
      </div>
      <vddl-list
        class="panel__body"
        :list="containedComponents"
        :drop="onDrop"
        :disable-if="isdisabled || !canEdit"
        :external-sources="true"
        :allowed-types="allowedTypes"
      >
        <list
          v-for="(col, number) in containedComponents"
          :id="col.id"
          :key="col.id"
          :list="containedComponents"
          :index="number"
          :disable-if="isdisabled || !canEdit"
          :container-id="component.id"
        />
      </vddl-list>
    </div>
    <div
      v-else
      class="item"
      :class="{ selected: isSelected }"
      :draggable="!isdisabled && canEdit"
      @contextmenu.prevent="onContextMenu($event)"
      @dragstart="onComponentStartDrag($event)"
    >
      <!-- <v-popover v-bind="options"> -->
      <div class="thumb" :class="{ hasimage: imageUrl }" @click="onClick">
        <img
          v-if="imageUrl"
          width="64"
          height="60"
          :src="imageUrl"
          @load="onImageLoad"
        />
        <svg-icon
          v-else-if="icon"
          ref="icon"
          :icon="icon"
          :width="'22'"
          :height="'22'"
          @on-load="onIconLoad"
        />
      </div>

      <div class="nameholder" @click="onClick">
        <input
          v-if="isEditing"
          ref="nameInput"
          class="nameinput"
          spellcheck="false"
          @blur="onBlurText($event, component)"
          @keyup.enter="onBlurText($event, component)"
        />
        <span
          v-else
          type="text"
          class="name"
          @dblclick="onDblclickText($event, component)"
        >
          {{ component.name }}
        </span>
      </div>
      <VDropdown
        v-if="canEdit"
        popper-class="v-popper--compListItem-dropdown"
        placement="bottom-start"
      >
        <button type="button" class="btn-add">
          <svg-icon :width="'11'" :height="'11'" icon="app-icons/create" />
        </button>
        <template #popper>
          <div class="menu">
            <button
              v-close-popper
              type="button"
              class="option"
              @click="onClickCreateComponent"
            >
              Create Component
            </button>
            <button
              v-close-popper
              type="button"
              class="option"
              @click="onClickCreateFolder"
            >
              Create Folder
            </button>
          </div>
        </template>
      </VDropdown>
    </div>
  </vddl-draggable>
</template>

<script>
import emitter from 'tiny-emitter/instance';
import { mapGetters, mapActions } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import SvgIcon from '../all/SvgIcon.vue';
import componentTypes from '../../vuex/project/types/components-types.js';
import { fittingString, round2DContextPath } from '../../canvas-utils.js';
import { loadImage, has } from '../../utils.js';
import updater from '../../vuex/project/updater.js';

export default {
  name: 'List',

  components: {
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    list: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: null,
    },
    name: {
      type: String,
      default: null,
    },
    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragCanvas: null,
      isdisabled: false,
      isEditing: false,
      isSelected: false,
      allowedTypes: [DRAG_TYPE.COMPONENT, DRAG_TYPE.CONTAINER],
      isPopoverActive: false,
      options: {
        placement: 'bottom-start',
        trigger: 'click',
        boundariesElement: document.body,
        delay: {
          show: 100,
          hide: 100,
        },
        popoverBaseClass: 'tooltip popover create_popup',
        popoverInnerClass: 'tooltip-inner popover-inner',
        popoverArrowClass: 'tooltip-arrow popover-arrow',
        offset: '0',
      },
    };
  },

  computed: {
    ...mapGetters('project/data/components', [
      'getIsRecursiveContainer',
      'getContainedNodes',
      'getCoverId',
      'getCoverIcon',
      'getDefaultIcon',
      'getContainerNode',
      'getContainerNodes',
      'getIndex',
      'getNode',
      'getRootContainer',
    ]),
    ...mapGetters('project/config/components', [
      'getSelections',
      'getIsCollapsed',
      'getOpen',
    ]),
    ...mapGetters('application/drag', ['getItems', 'getSource', 'getTarget']),
    ...mapGetters('project/data/assets', ['getTinyImageUrl']),

    /**
     * Returns the current component
     * @returns {Object}
     */
    component() {
      return this.getNode(this.id);
    },

    /**
     * Returns the component drag type
     * @return {String}
     */
    type() {
      return this.getIsRecursiveContainer(this.id)
        ? DRAG_TYPE.CONTAINER
        : DRAG_TYPE.COMPONENT;
    },

    /**
     * Returns the components that are contained in this component
     * @returns {Array}
     */
    containedComponents() {
      return this.getContainedNodes(
        this.component.id,
        'components',
        'children'
      );
    },

    /**
     * Returns the selected component id
     * @returns {String}
     */
    selectedId() {
      return this.getSelections.length === 0 ? null : this.getSelections[0];
    },

    /**
     * Return the cover asset id
     * @returns {String|null}
     */
    coverId() {
      return this.component.assets?.cover?.id;
    },

    /**
     * Return the small image url for the cover asset
     * @returns {String|null}
     */
    imageUrl() {
      return this.getTinyImageUrl(this.coverId);
    },

    /**
     * Return the icon for the cover asset
     * @returns {String|null}
     */
    icon() {
      if (this.component.assets?.cover?.type === 'icon') {
        return this.component.assets?.cover?.file;
      }
      return this.getDefaultIcon;
    },

    /**
     * Return whether the component is collapsed or not
     * @returns {Boolean}
     */
    isCollapsed() {
      return this.getIsCollapsed(this.id);
    },

    /**
     * Return whether the component has children
     * @returns {Boolean}
     */
    hasChildren() {
      return this.containedComponents.length > 0;
    },
  },

  watch: {
    /**
     * Watch whether item is selected.
     */
    getOpen(newValue, oldValue) {
      if (this.id === newValue) {
        // get all container ancestor ids
        const containerIds = this.getContainerNodes(
          newValue,
          'components',
          'children'
        );
        // Expand all container ancestors
        containerIds.forEach(id => {
          this.setCollapsed({
            id,
            value: false,
            apply: false,
          });
        });
        updater.complete({ apply: true });

        // Scroll to item position
        this.$nextTick(() => {
          this.$el.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        });
      }
    },
  },

  created() {
    emitter.on(EVENT.DISABLE_LIST_ITEM, (el, event) => {
      this.isdisabled = true;
    });
    emitter.on(EVENT.ENABLE_LIST_ITEM, (el, event) => {
      this.isdisabled = false;
    });
  },

  methods: {
    ...mapActions('project/data/components', [
      'customCreate',
      'customUpdate',
      'customDelete',
      'duplicate',
      'setCover',
      'move',
    ]),
    ...mapActions('project/config/components', [
      'setOpen',
      'setCollapsed',
      'select',
    ]),
    ...mapActions('project/data/elements', {
      addElementComponent: 'addComponent',
    }),
    ...mapActions('project/data/attributes', {
      addAttributeComponent: 'addComponent',
    }),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
      'setOnDeleteMenu',
    ]),
    ...mapActions('application/drag', [
      'setItems',
      'setSource',
      'setTarget',
      'clear',
    ]),
    ...mapActions('application/modals', ['setComponentPropertiesModal']),

    /**
     * Custom event from svg icon load
     */
    async onIconLoad(svg) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.assetImage = await loadImage(url);
    },

    /**
     * Image onLoad event
     */
    onImageLoad() {
      console.log('onImageLoad');
      this.assetImage = new Image();
      this.assetImage.src = this.imageUrl;
    },

    /**
     * Toggle between expansion or collpase of the container
     */
    toggle(component) {
      if (this.isEditing) return;
      this.setCollapsed({
        id: component.id,
        value: !this.isCollapsed,
        apply: true,
      });
    },

    /**
     * Event handler for vddl dragstart event.
     */
    onDragStart(draggable) {
      // Add class to body for hover fix
      document.body.classList.add('dragging');

      // Store source drag information
      this.setItems({ value: { type: this.type, ids: [this.id] } });
      this.setSource({
        value: {
          type: DRAG_LIST.COMPONENTS,
          ids: [this.containerId],
          indexes: [this.index],
        },
      });
    },

    /**
     * Event handler for vddl dragend event.
     */
    onDragEnd(event) {
      // Add class to body for hover fix
      document.body.classList.remove('dragging');

      // Remove any drag canvas
      $('.dragCanvasSidebar').remove();
    },

    onComponentStartDrag($event) {
      if (!this.canEdit) return;

      const canvas = document.createElement('canvas');
      canvas.classList.add('dragCanvasSidebar');
      canvas.width = 200;
      canvas.height = 32;

      // This is required in Chrome (check styles below)
      document.body.append(canvas);
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (this.imageUrl) {
        const image = this.assetImage;

        // Compute the minimum dimension in order to crop the image
        const minDimension =
          image.width < image.height ? image.width : image.height;

        // Offsets for image cropping
        const sx = (image.width - minDimension) / 2;
        const sy = (image.height - minDimension) / 2;

        // Save current canvas state as we will begin masking the image
        ctx.save();

        // Create the mask
        round2DContextPath(ctx, 8, 5, 22, 22, 11);
        ctx.clip();
        // Draw the image on the canvas
        ctx.drawImage(image, sx, sy, minDimension, minDimension, 8, 5, 22, 22);

        // Restore canvas
        ctx.restore();
      } else if (this.icon) {
        if (this.icon === 'app-icons/folder') {
          ctx.drawImage(this.assetImage, 12, 6, 16, 16);
        } else {
          ctx.drawImage(this.assetImage, 14, 6, 18, 18);
        }
      }

      // Draw text
      ctx.font = '12px "Open Sans",sans-serif';
      ctx.fillStyle = 'white';
      ctx.fillText(fittingString(ctx, this.component.name, 150), 40, 20);

      $event.dataTransfer.setDragImage(
        canvas,
        canvas.width / 2,
        canvas.height / 2
      );
    },

    /**
     * Event handler for vddl drop event. This is triggered before vddl move event on the item.
     */
    onDrop(draggable) {
      this.setTarget({
        value: {
          type: DRAG_LIST.COMPONENTS,
          id:
            this.component === undefined
              ? this.getRootContainer.id
              : this.component.id,
          index: draggable.index,
        },
      });
    },

    /**
     * Event handler for vddl move event. The component has been moved to a correct position.
     */
    onMove(event) {
      const items = this.getItems;
      const source = this.getSource;
      const target = this.getTarget;
      console.log('onMove called. Target:', target);
      // In case it was dropped on the component tree perform move
      if (target.type === DRAG_LIST.COMPONENTS) {
        this.move({
          id: items.ids[0],
          tId: target.id,
          tIndex: target.index,
          apply: true,
        });
      } else if (target.type === DRAG_LIST.ELEMENTS) {
        this.addElementComponent({
          id: target.id,
          componentId: items.ids[0],
          index: target.index,
          apply: true,
        });
      } else if (target.type === DRAG_LIST.ATTRIBUTES) {
        this.addAttributeComponent({
          id: target.id,
          componentId: items.ids[0],
          index: target.index,
          apply: true,
        });
      }
      // Clear drag information
      this.clear();
    },

    /**
     * Event handler for vddl cancel event. The drag has been canceled.
     */
    onCancel(event) {
      this.clear();
    },

    /**
     * Event handler for click event. A component was clicked - open it for editing (or viewing)
     */
    onClick() {
      // Do not open if clicked while editing
      if (this.isEditing) return;
      this.setOpen({ id: this.component.id, apply: true });
    },

    onClickCreateComponent() {
      this.createComponent();
    },

    createComponent() {
      // Get default container
      let container = this.getContainerNode(this.id, 'components', 'children');
      // Get default tIndex
      let tIndex = this.getIndex(this.id, 'components', 'children');
      if (this.getIsRecursiveContainer(this.id)) {
        // Get Current item instead of container ( current item as container and tIndex 0 )
        container = this.getNode(this.id);
        tIndex = 0;
      }
      this.customCreate({
        component: componentTypes.newItem,
        tId: container.id,
        tIndex,
        tExpand: true,
        apply: false,
      }).then(id => {
        // Edit the component
        this.setOpen({ id, apply: true });
      });
    },

    onClickCreateFolder() {
      this.createFolder();
    },

    createFolder() {
      // Get default container
      let container = this.getContainerNode(this.id, 'components', 'children');
      // Get default tIndex
      let tIndex = this.getIndex(this.id, 'components', 'children');
      if (this.getIsRecursiveContainer(this.id)) {
        // Get Current item instead of container ( current item as container and tIndex 0 )
        container = this.getNode(this.id);
        tIndex = 0;
      }
      this.customCreate({
        component: componentTypes.newContainer,
        tId: container.id,
        tIndex,
        tExpand: true,
        apply: true,
      });
    },

    /**
     * Event handler for double click on text event.
     */
    onDblclickText($event, component) {
      if (this.canEdit) {
        this.setRenameMode();
      }
    },

    /**
     * Event handler for text blur event.
     */
    onBlurText($event, component) {
      const name = $event.target.value;
      this.customUpdate({
        id: this.id,
        props: { name: name || 'Untitled component' },
        apply: true,
      });
      // Destroy input
      this.isEditing = false;
      // Enable dragndrop
      emitter.emit(EVENT.ENABLE_LIST_ITEM);
    },

    /**
     * Event handler for context menu event.
     */
    onContextMenu(event) {
      if (this.canEdit) {
        this.isSelected = true;
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Set this item in rename mode.
     */
    setRenameMode() {
      // Create input
      this.isEditing = true;

      // Disable dragndrop
      emitter.emit(EVENT.DISABLE_LIST_ITEM);

      // Because of v-if on isEditing the DOM is not rendered yet
      this.$nextTick(() => {
        const { nameInput } = this.$refs;
        nameInput.value = this.component.name;
        nameInput.focus();
        // Set the selection range (instead of select()) to cover Safari also
        nameInput.setSelectionRange(0, nameInput.value.length);
      });
    },

    openComponentProperties() {
      this.setComponentPropertiesModal({ value: this.id });
    },

    /**
     * Create a context menu for this component
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      this.createItem({
        name: 'Create Component',
        icon: null,
        commands: {
          doAction: () => {
            this.createComponent();
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      this.createItem({
        name: 'Create Folder',
        icon: null,
        commands: {
          doAction: () => {
            this.createFolder();
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      this.createItem({
        name: 'Duplicate',
        icon: null,
        commands: {
          doAction: () => {
            // Put the duplicate inside the current container and just above the original
            const container = this.getContainerNode(
              this.id,
              'components',
              'children'
            );
            const tIndex = this.getIndex(this.id, 'components', 'children');
            this.duplicate({
              id: this.id,
              tId: container.id,
              tIndex,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });
      if (!this.getIsRecursiveContainer(this.id)) {
        this.createItem({
          name: 'References...',
          icon: null,
          commands: {
            doAction: () => {
              this.openComponentProperties();
              this.deleteMenu();
            },
            isActive: () => {
              return true;
            },
            isEnabled: () => {
              return true;
            },
          },
        });
      }
      this.createItem({
        name: 'Rename',
        icon: null,
        commands: {
          doAction: () => {
            this.setRenameMode();
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
      });
      this.createItem({
        name: 'Delete',
        icon: null,
        commands: {
          doAction: () => {
            this.customDelete({
              id: this.id,
              apply: true,
            });
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });

      this.setOnDeleteMenu({
        onDeleteMenu: () => {
          this.isSelected = false;
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import './SidebarItem.scss';

.vddl-draggable,
.vddl-list {
  position: relative;
}

.vddl-list {
  padding-left: 0;
  // min-height: 5px;
}

// .panel__body {
//     min-height: 5px;
// }

.panel__body > .panel {
  margin: 10px;
}

.component,
.panel__body .panel__placeholder {
  width: 100%;
  // min-height: 5px;
  box-sizing: border-box;
}

.component.no-padding-left {
  padding-left: 0;
}

.component:last-child {
  border-bottom: none;
}

.panel.panel--info .panel__body {
  padding: 25px;
}

.nodrag {
  -ms-flex: 1;
  flex: 1;
  display: -ms-flexbox;
  display: flex;
}

.panel__body--list.padding {
  padding: 7px;
}

.panel__body--list.padding:after {
  content: '';
  clear: both;
  display: block;
  width: 100%;
  height: 0;
  visibility: hidden;
}

.btn-add {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  width: 26px;
  height: 26px;
  cursor: pointer;
  border-radius: 4px;

  &[data-popper-shown] {
    background: #2c3132;
  }
}
</style>

<style lang="scss">
.v-popper__popper.v-popper--theme-dropdown.v-popper--compListItem-dropdown {
  .v-popper__inner {
    background: #0d0e0e;
    border: 1px solid #414c4d;
    border-radius: 3px;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    padding: 0;
    color: #bcbcbc;
    margin-top: -7px;
  }
  .v-popper__arrow-container {
    display: none;
  }

  .menu {
    .option {
      align-items: center;
      cursor: pointer;
      display: flex;
      padding: 10px 16px;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      border: none;
      color: #bcbcbc;
      width: 100%;
      font-size: 13px;

      &:hover {
        background: #363e3f;
      }
    }
  }
}
</style>
